import { BaseValidator } from '../BaseValidator';
import { FormValidationResult, FieldValidationResult } from '../types';

/**
 * Form-level validator that validates multiple fields at once
 * Provides comprehensive form validation with field-level and global validation
 */
export class FormValidator {
  private fieldValidators: Map<string, BaseValidator<unknown>> = new Map();
  private globalValidators: BaseValidator<unknown>[] = [];

  /**
   * Add a validator for a specific field
   */
  field<T = unknown>(name: string, validator: BaseValidator<T>): this {
    this.fieldValidators.set(name, validator as BaseValidator<unknown>);
    return this;
  }

  /**
   * Add a global validator that validates the entire form
   */
  global<T = unknown>(validator: BaseValidator<T>): this {
    this.globalValidators.push(validator as BaseValidator<unknown>);
    return this;
  }

  /**
   * Validate an entire form
   */
  validate(formData: Record<string, unknown>, context?: Record<string, unknown>): FormValidationResult {
    const fields: Record<string, FieldValidationResult> = {};
    let isFormValid = true;

    // Validate each field
    for (const [fieldName, validator] of this.fieldValidators) {
      const value = formData[fieldName];
      const result = validator.validate(value, context);
      
      fields[fieldName] = {
        field: fieldName,
        value,
        isValid: result.isValid,
        errors: result.errors,
        warnings: result.warnings
      };

      if (!result.isValid) {
        isFormValid = false;
      }
    }

    // Run global validation
    const globalErrors: string[] = [];
    for (const validator of this.globalValidators) {
      const result = validator.validate(formData, context);
      if (!result.isValid) {
        globalErrors.push(...result.errors);
        isFormValid = false;
      }
    }

    return {
      isValid: isFormValid,
      fields,
      globalErrors: globalErrors.length > 0 ? globalErrors : undefined
    };
  }

  /**
   * Validate a single field
   */
  validateField(fieldName: string, value: Record<string, unknown>, context?: Record<string, unknown>): FieldValidationResult {
    const validator = this.fieldValidators.get(fieldName);
    if (!validator) {
      return {
        field: fieldName,
        value,
        isValid: true,
        errors: []
      };
    }

    const result = validator.validate(value, context);
    return {
      field: fieldName,
      value,
      isValid: result.isValid,
      errors: result.errors,
      warnings: result.warnings
    };
  }

  /**
   * Check if a specific field has a validator
   */
  hasField(fieldName: string): boolean {
    return this.fieldValidators.has(fieldName);
  }

  /**
   * Get all field names that have validators
   */
  getFieldNames(): string[] {
    return Array.from(this.fieldValidators.keys());
  }

  /**
   * Static factory method
   */
  static create(): FormValidator {
    return new FormValidator();
  }

  /**
   * Create a validator with fields
   */
  static withFields<T extends Record<string, BaseValidator<unknown>>>(fields: T): FormValidator {
    const validator = new FormValidator();
    for (const [name, fieldValidator] of Object.entries(fields)) {
      validator.field(name, fieldValidator);
    }
    return validator;
  }
}